import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { Chip } from '@mui/material';
import FormButton from '../../components/common/stories/FormButton';
import { ROLES } from '../../context/UserContext';

const AcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();

  const [invitation, setInvitation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAccepting, setIsAccepting] = useState(false);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/invitations/${token}`
        );
        setInvitation(response.data);
      } catch (err) {
        console.error('Error fetching invitation:', err);
        setError(err.response?.data?.error || 'Invalid or expired invitation');
      } finally {
        setIsLoading(false);
      }
    };

    if (token) {
      fetchInvitation();
    }
  }, [token]);

  const getRoleLabel = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN: return 'Super Admin';
      case ROLES.ADMIN: return 'Admin';
      case ROLES.AGENT: return 'Agent';
      default: return role;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case ROLES.SUPER_ADMIN: return 'secondary';
      case ROLES.ADMIN: return 'primary';
      case ROLES.AGENT: return 'success';
      default: return 'default';
    }
  };

  const handleAcceptInvitation = async () => {
    if (!isAuthenticated) {
      // Store token in localStorage and redirect to login
      localStorage.setItem('pendingInvitationToken', token);
      loginWithRedirect({
        appState: { returnTo: `/accept-invitation/${token}` },
        authorizationParams: {
          login_hint: invitation?.email,
        },
      });
      return;
    }

    try {
      setIsAccepting(true);
      const authToken = await getAccessTokenSilently();

      await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/users/invitations/${token}/accept`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      // Clear pending invitation token
      localStorage.removeItem('pendingInvitationToken');

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('Error accepting invitation:', err);
      setError(err.response?.data?.error || 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  // Check for pending invitation after login
  useEffect(() => {
    const pendingToken = localStorage.getItem('pendingInvitationToken');
    if (isAuthenticated && pendingToken && pendingToken === token && invitation?.status === 'pending') {
      handleAcceptInvitation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, token, invitation?.status]);

  if (isLoading) {
    return (
      <div className="invitation-page">
        <div className="container">
          <div className="invitation-card">
            <h2>Loading invitation...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invitation-page">
        <div className="container">
          <div className="invitation-card">
            <h2>Invitation Error</h2>
            <p className="error-message">{error}</p>
            <FormButton onClick={() => navigate('/')}>
              Go to Home
            </FormButton>
          </div>
        </div>
      </div>
    );
  }

  if (invitation?.status !== 'pending') {
    return (
      <div className="invitation-page">
        <div className="container">
          <div className="invitation-card">
            <h2>Invitation Not Available</h2>
            <p>
              This invitation has already been {invitation?.status === 'accepted' ? 'accepted' : invitation?.status}.
            </p>
            {isAuthenticated ? (
              <FormButton onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </FormButton>
            ) : (
              <FormButton onClick={() => loginWithRedirect()}>
                Login
              </FormButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-page">
      <div className="container">
        <div className="invitation-card">
          <div className="invitation-logo">
            <img src="/adgro-logo-color.png" alt="Logo" style={{ maxWidth: '150px' }} />
          </div>

          <h2>You're Invited!</h2>

          <div className="invitation-details">
            <p>
              You've been invited to join <strong>{invitation.organization_name || 'the organization'}</strong> as:
            </p>

            <div className="role-badge" style={{ margin: '20px 0' }}>
              <Chip
                label={getRoleLabel(invitation.role)}
                color={getRoleColor(invitation.role)}
                size="medium"
              />
            </div>

            {invitation.role === ROLES.AGENT && invitation.locations?.length > 0 && (
              <div className="assigned-locations">
                <p>You will have access to:</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '8px' }}>
                  {invitation.locations.map((loc, idx) => (
                    <Chip
                      key={idx}
                      label={loc.store_name || loc}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </div>
              </div>
            )}

            <p className="invited-email" style={{ marginTop: '20px', color: '#666' }}>
              Invitation sent to: <strong>{invitation.email}</strong>
            </p>
          </div>

          <div className="invitation-actions" style={{ marginTop: '30px' }}>
            {isAuthenticated ? (
              <>
                {user?.email === invitation.email ? (
                  <FormButton
                    onClick={handleAcceptInvitation}
                    disabled={isAccepting}
                  >
                    {isAccepting ? 'Accepting...' : 'Accept Invitation'}
                  </FormButton>
                ) : (
                  <div>
                    <p className="warning-message" style={{ color: '#f57c00', marginBottom: '16px' }}>
                      You are logged in as <strong>{user?.email}</strong>, but this invitation was sent to <strong>{invitation.email}</strong>.
                    </p>
                    <FormButton
                      onClick={() => {
                        localStorage.setItem('pendingInvitationToken', token);
                        loginWithRedirect({
                          authorizationParams: {
                            login_hint: invitation.email,
                          },
                        });
                      }}
                    >
                      Login as {invitation.email}
                    </FormButton>
                  </div>
                )}
              </>
            ) : (
              <div>
                <p style={{ marginBottom: '16px', color: '#666' }}>
                  Please sign in or create an account to accept this invitation.
                </p>
                <FormButton onClick={handleAcceptInvitation}>
                  Sign In / Sign Up
                </FormButton>
              </div>
            )}
          </div>

          <p className="expiry-notice" style={{ marginTop: '20px', fontSize: '12px', color: '#999' }}>
            This invitation expires on {new Date(invitation.expires_at).toLocaleDateString()}
          </p>
        </div>
      </div>

      <style>{`
        .invitation-page {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          padding: 20px;
        }

        .invitation-card {
          background: white;
          border-radius: 12px;
          padding: 40px;
          max-width: 500px;
          width: 100%;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .invitation-card h2 {
          margin-bottom: 20px;
          color: #333;
        }

        .error-message {
          color: #f44336;
          margin-bottom: 20px;
        }
      `}</style>
    </div>
  );
};

export default AcceptInvitation;
